import React from 'react';
import RowActionsMenu, { RowAction } from './RowActionsMenu';

export interface DataTableColumn<T> {
  id: string;
  header: React.ReactNode;
  accessor?: (row: T) => React.ReactNode;
  render?: (row: T) => React.ReactNode;
  width?: string | number;
  align?: 'left' | 'right' | 'center';
  className?: string;
  /** Hide on smaller viewports */
  hideBelow?: 'sm' | 'md' | 'lg';
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Build the per-row action menu items */
  rowActions?: (row: T) => RowAction[];
  /** Visible in an extra right-most cell, alongside the "…" menu */
  inlineRowActions?: (row: T) => React.ReactNode;
  empty?: React.ReactNode;
  loading?: boolean;
  skeletonRows?: number;
  selectable?: boolean;
  selectedIds?: Set<string>;
  onToggleRow?: (id: string) => void;
  onToggleAll?: () => void;
  stickyHeader?: boolean;
  className?: string;
}

const hideBelowClass = {
  sm: 'ui-col-hide-sm',
  md: 'ui-col-hide-md',
  lg: 'ui-col-hide-lg',
} as const;

export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  onRowClick,
  rowActions,
  inlineRowActions,
  empty,
  loading,
  skeletonRows = 5,
  selectable,
  selectedIds,
  onToggleRow,
  onToggleAll,
  stickyHeader,
  className = '',
}: DataTableProps<T>) {
  const totalCols =
    columns.length + (selectable ? 1 : 0) + (rowActions || inlineRowActions ? 1 : 0);

  const allSelected =
    selectable && selectedIds && rows.length > 0 && rows.every((r) => selectedIds.has(getRowKey(r)));

  return (
    <div className={`ui-table-wrapper ${className}`.trim()}>
      <table className={`ui-table ${stickyHeader ? 'ui-table-sticky' : ''}`.trim()}>
        <thead className="ui-table-head">
          <tr>
            {selectable && (
              <th className="ui-col-checkbox">
                <input
                  type="checkbox"
                  className="table-checkbox"
                  checked={!!allSelected}
                  onChange={() => onToggleAll?.()}
                  aria-label="Select all rows"
                />
              </th>
            )}
            {columns.map((c) => (
              <th
                key={c.id}
                className={[
                  `ui-col-${c.id}`,
                  c.align ? `ui-align-${c.align}` : '',
                  c.hideBelow ? hideBelowClass[c.hideBelow] : '',
                  c.className || '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={c.width ? { width: c.width } : undefined}
              >
                {c.header}
              </th>
            ))}
            {(rowActions || inlineRowActions) && <th className="ui-col-actions" aria-label="Actions" />}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: skeletonRows }).map((_, i) => (
              <tr key={`sk-${i}`} className="ui-table-row ui-table-row-skeleton">
                <td colSpan={totalCols}>
                  <div className="ui-skeleton-bar" />
                </td>
              </tr>
            ))
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={totalCols} className="ui-table-empty-cell">
                {empty ?? (
                  <div className="ui-table-empty-default">No records to display.</div>
                )}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const key = getRowKey(row);
              const actions = rowActions?.(row);
              return (
                <tr
                  key={key}
                  className={[
                    'ui-table-row',
                    onRowClick ? 'ui-table-row-clickable' : '',
                    selectedIds?.has(key) ? 'ui-table-row-selected' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {selectable && (
                    <td className="ui-col-checkbox" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="table-checkbox"
                        checked={!!selectedIds?.has(key)}
                        onChange={() => onToggleRow?.(key)}
                        aria-label="Select row"
                      />
                    </td>
                  )}
                  {columns.map((c) => (
                    <td
                      key={c.id}
                      className={[
                        `ui-col-${c.id}`,
                        c.align ? `ui-align-${c.align}` : '',
                        c.hideBelow ? hideBelowClass[c.hideBelow] : '',
                        c.className || '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {c.render ? c.render(row) : c.accessor ? c.accessor(row) : null}
                    </td>
                  ))}
                  {(rowActions || inlineRowActions) && (
                    <td
                      className="ui-col-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="ui-row-actions-cell">
                        {inlineRowActions?.(row)}
                        {actions && actions.length > 0 && <RowActionsMenu actions={actions} />}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

export default DataTable;
